import { useStore } from "../store";
import { Scope } from "../types";
import { COMPONENTS } from "./registry";

/**
 * Generic component dispatcher.
 *
 * Looks up the component by id from the store, resolves its declared type
 * (the one key in componentProperties), and renders the matching React
 * component from the registry. Unknown types render an explicit placeholder
 * so missing renderer support is visible, not silent.
 */
export function ComponentRenderer({ id, scope }: { id: string; scope: Scope }) {
  const component = useStore((s) => s.components.get(id));
  if (!component) {
    return <Placeholder>Missing component: {id}</Placeholder>;
  }

  const propEntries = Object.entries(component.componentProperties);
  if (propEntries.length === 0) {
    return <Placeholder>Empty component: {id}</Placeholder>;
  }
  // A2UI v0.9: each component declares exactly one type entry under componentProperties.
  const [type, fields] = propEntries[0]!;
  const Impl = COMPONENTS[type];
  if (!Impl) {
    return <Placeholder>Unsupported component type: {type}</Placeholder>;
  }
  return <Impl id={id} fields={fields} scope={scope} />;
}

export function ChildById({ id, scope }: { id: string | null; scope: Scope }) {
  if (!id) return null;
  return <ComponentRenderer id={id} scope={scope} />;
}

function Placeholder({ children }: { children: React.ReactNode }) {
  return <div className="a2-placeholder">⚠ {children}</div>;
}
